import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../../services/api';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Log component mount for debugging
  useEffect(() => {
    try {
      setMounted(true);
      console.log('Login component mounted successfully');
    } catch (err) {
      console.error('Error initializing Login component:', err);
      setError('Failed to initialize login form. Please refresh the page.');
    }
  }, []);

  const handleChange = (e) => {
    try {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
      });
      setError('');
    } catch (err) {
      console.error('Error in handleChange:', err);
      setError('An error occurred. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.login(formData);
      
      // Debug: Log the full response to see what we're actually receiving
      console.log('Login API Response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        dataType: typeof response.data,
        headers: response.headers
      });
      
      // Check if response.data exists
      if (!response.data) {
        console.error('Login failed: No data in response');
        setError('Login failed. No data received from server.');
        return;
      }
      
      // Check if response is HTML (happens when /api is rewritten to index.html)
      if (typeof response.data === 'string') {
        const isHTML = response.data.trim().startsWith('<!DOCTYPE') || 
                       response.data.trim().startsWith('<html') ||
                       response.data.includes('<html');
        if (isHTML) {
          const baseURL = response.config?.baseURL ?? '';
          const isRelativeApi = !baseURL || baseURL.startsWith('/');
          console.error('Login failed: Received HTML instead of JSON. API URL might be incorrect or proxied incorrectly.');
          setError(isRelativeApi
            ? 'API returned HTML. If testing locally, use npm run build:test. If this is the live site, the server proxy or backend may be down—check /api/health.'
            : 'API returned HTML. Check that the backend is running and reachable.');
          return;
        }
      }
      
      // Check if response.data is an object (JSON response)
      if (typeof response.data !== 'object') {
        console.error('Login failed: Invalid response data type:', typeof response.data);
        setError(`Login failed. Invalid response format: ${typeof response.data}`);
        return;
      }
      
      // Check if login was successful - handle both success field and token presence
      if (response.data.success || response.data.token) {
        const token = response.data.token;
        if (!token) {
          console.warn('Login response has success=true but no token');
          setError('Login failed. Token missing from response.');
          return;
        }
        localStorage.setItem('adminToken', token);
        console.log('Login successful, redirecting to admin blog');
        // Redirect to admin blog manager
        navigate('/admin/blog');
      } else {
        console.error('Login failed: Response does not contain success or token', response.data);
        setError('Login failed. Invalid response from server.');
      }
    } catch (err) {
      console.error('Login error:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        request: err.request,
        config: err.config
      });
      
      if (err.response) {
        // Server responded with error status
        const errorData = err.response.data;
        console.error('Error response data:', errorData);
        
        // Check if error response is HTML
        if (typeof errorData === 'string' && 
            (errorData.trim().startsWith('<!DOCTYPE') || errorData.includes('<html'))) {
          const baseURL = err.config?.baseURL ?? '';
          const isRelativeApi = !baseURL || baseURL.startsWith('/');
          setError(isRelativeApi
            ? 'API returned HTML. If testing locally, use npm run build:test. If this is the live site, the server proxy or backend may be down—check /api/health.'
            : 'API returned HTML. Check that the backend is running and reachable.');
        } else {
          setError(errorData?.error || errorData?.message || 'Login failed. Please check your credentials.');
        }
      } else if (err.request) {
        // Request was made but no response received
        console.error('No response received. Request details:', err.request);
        setError('Cannot connect to server. Please make sure the backend is running and accessible.');
      } else {
        // Something else happened
        console.error('Unexpected error:', err);
        setError(`Login failed: ${err.message || 'Please try again.'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while component initializes
  if (!mounted) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Admin Login
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            SwiftToolsKit Admin Panel
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded">
              {error}
            </div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-primary hover:bg-gradient-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Logging in...' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
